/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import { ActivityIndicator, Pressable, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors';
import { appToastConfig, toastTopOffset } from '../../../shared/ui/appToast';
import { styles } from '../posShared.styles';
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { useModalBackdropPadding } from '../../../shared/ui/hooks-ui/useModalBackdropPadding';
import { PosCheckoutBody } from './PosCheckoutBody';
import { PosCheckoutSuccess } from './PosCheckoutSuccess';
import { resolveCheckoutLayoutWide, resolveCheckoutShellMaxHeight } from './posCheckoutDerived';
import { usePosCheckoutContextOptional } from '../contexts/PosCheckoutContext';
import { usePosCheckoutStore } from '../../store/usePosCheckoutStore';
import type { PosCheckoutModalProps } from '../contexts/PosCheckoutContext';
import { ensureTicketFonts } from '../../../shared/ticket/ensureTicketFonts';

export type { PosCheckoutModalProps } from '../contexts/PosCheckoutContext';

function checkoutTitle(itemCount: number): string {
  if (itemCount <= 0) return 'Cobrar';
  return `Cobrar \u00b7 ${itemCount === 1 ? '1 \u00edtem' : `${itemCount} \u00edtems`}`;
}

export const PosCheckoutModal = ({ visible, checkoutLoading, onClose, onRequestClose, overlayModal }: PosCheckoutModalProps) => {
  const modalBackdropPadding = useModalBackdropPadding();
  const insets = useSafeAreaInsets();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const layoutWide = resolveCheckoutLayoutWide(windowWidth, windowHeight);
  const checkoutShellMaxHeight = resolveCheckoutShellMaxHeight(windowHeight);
  const checkout = usePosCheckoutContextOptional();
  const saleCompleted = usePosCheckoutStore((s) => s.saleCompleted);
  const itemCount = checkout?.cart?.length ?? 0;
  const title = saleCompleted ? 'Venta cobrada' : checkoutTitle(itemCount);
  const [detailExpanded, setDetailExpanded] = useState(true);
  const [fiscalExpanded, setFiscalExpanded] = useState(true);

  useEffect(() => {
    if (visible) {
      ensureTicketFonts();
      // Desktop muestra la tabla abierta; en mobile el detalle va colapsado
      // para que el sheet quede corto (hero → medios → monto → confirmar).
      setDetailExpanded(layoutWide);
      setFiscalExpanded(layoutWide);
    }
  }, [visible, layoutWide]);

  // Solo mientras el modal está visible: si queda montado al cerrar, Toast.show
  // sigue apuntando acá y el toast de éxito post-cobro se pierde con el slide-out.
  const toastHost = visible ? (
    <Toast config={appToastConfig} topOffset={toastTopOffset(insets.top)} />
  ) : null;

  if (layoutWide) {
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={onRequestClose ?? onClose} accessibilityViewIsModal>
        <View style={[styles.modalBackdrop, modalBackdropPadding, modalExperience.modalRoot]}>
          <View
            style={[
              styles.modalCard,
              styles.checkoutCard,
              styles.checkoutModalShell,
              modalExperience.modalWidthComfortable,
              styles.checkoutModalShellWide,
              // En éxito el contenido es chico: alto auto, no el shell completo.
              saleCompleted
                ? { maxHeight: checkoutShellMaxHeight }
                : { height: checkoutShellMaxHeight, maxHeight: checkoutShellMaxHeight },
            ]}
            testID="pos.checkout.modal"
          >
            <View style={styles.wideHeader}>
              <Text style={styles.wideHeaderTitle}>{title}</Text>
              <Pressable onPress={onClose} style={styles.wideCloseBtn} accessibilityRole="button" accessibilityLabel="Cerrar cobro" testID="pos.checkout.close">
                <Ionicons name="close" size={18} color={ticketColors.ink3} />
              </Pressable>
            </View>

            {checkoutLoading ? (
              <View style={styles.loadingBox} testID="pos.checkout.loading">
                <ActivityIndicator color={ticketColors.gold} />
                <Text style={styles.caption}>Armando el cobro…</Text>
              </View>
            ) : saleCompleted ? (
              <PosCheckoutSuccess summary={saleCompleted} />
            ) : (
              <View style={styles.checkoutModalBodyHost}>
                <PosCheckoutBody
                  visible={visible}
                  layoutWide={layoutWide}
                  detailExpanded={detailExpanded}
                  onToggleDetail={() => setDetailExpanded((v) => !v)}
                  fiscalExpanded={fiscalExpanded}
                  onToggleFiscal={() => setFiscalExpanded((v) => !v)}
                />
              </View>
            )}
          </View>
          {toastHost}
          {overlayModal}
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onRequestClose ?? onClose} accessibilityViewIsModal>
      <View style={styles.sheetBackdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        <View
          style={[styles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm), maxHeight: checkoutShellMaxHeight }]}
          testID="pos.checkout.modal"
        >
          <View style={styles.grabber} />

          <View style={styles.sheetHeader}>
            <Text style={styles.sheetTitle}>{title}</Text>
            <Pressable onPress={onClose} style={styles.sheetCloseBtn} accessibilityRole="button" accessibilityLabel="Cerrar cobro" testID="pos.checkout.close">
              <Ionicons name="close" size={16} color={ticketColors.ink3} />
            </Pressable>
          </View>

          {checkoutLoading ? (
            <View style={styles.loadingBox} testID="pos.checkout.loading">
              <ActivityIndicator color={ticketColors.gold} />
              <Text style={styles.caption}>Armando el cobro…</Text>
            </View>
          ) : saleCompleted ? (
            <PosCheckoutSuccess summary={saleCompleted} />
          ) : (
            <View style={styles.checkoutModalBodyHost}>
              <PosCheckoutBody
                visible={visible}
                layoutWide={layoutWide}
                detailExpanded={detailExpanded}
                onToggleDetail={() => setDetailExpanded((v) => !v)}
                fiscalExpanded={fiscalExpanded}
                onToggleFiscal={() => setFiscalExpanded((v) => !v)}
              />
            </View>
          )}
        </View>
        {toastHost}
        {overlayModal}
      </View>
    </Modal>
  );
};
